// @flow
import { annotateFacetExclusions, getUserExcludedFacets } from './searchExclusions';
import { getAuthToken, getZipCodeGeoCoordinates } from './user';
import {
    LOAD_CATEGORY_RESULTS_FAIL,
    LOAD_CATEGORY_RESULTS_REQUEST,
    LOAD_CATEGORY_RESULTS_SUCCESS,
} from './landingPage';
import {
    LOAD_SEARCH_RESULTS_FAIL,
    LOAD_SEARCH_RESULTS_REQUEST,
    LOAD_SEARCH_RESULTS_SUCCESS,
    SET_USE_GEO_LOCATION,
} from '../../utils/searchUtils';
import api from '../api/search';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import facetTypes from '../../utils/search/facetTypes';
import { getDeployment } from './config';
import { getItemCardDatas } from './cardData';
import { getRawSearchFacets } from './searchFacets';
import type { GlobalState } from '../rootReducer';
import { searchPromoted } from './searchPromoted';

/* reducer */
const DEFAULT_STATE = {
    error: false,
    facets: [],
    isLoading: true,
    itemIds: [],
    soldItemFacets: [],
    sort: [],
    submitted: false,
    success: false,
    totalFound: 0,
    useGeoLocation: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SEARCH_RESULTS_REQUEST:
        case LOAD_CATEGORY_RESULTS_REQUEST:
            return {
                ...state,
                error: false,
                submitted: true,
                success: false,
            };
        case LOAD_SEARCH_RESULTS_SUCCESS:
        case LOAD_CATEGORY_RESULTS_SUCCESS:
            return {
                ...state,
                facets: action.payload.facets,
                isLoading: false,
                itemIds: action.payload.itemIDs,
                soldItemFacets: action.payload.soldItemFacets,
                soldItemIds: action.payload.soldItemIDs,
                sort: action.payload.sort.filter(sortOption => sortOption !== 'publishDate'),
                submitted: false,
                success: true,
                totalBuyNowItems: action.payload.totalBuyNow,
                totalFound: action.payload.totalFound,
                totalLiveItems: action.payload.totalLive,
                totalPages: action.payload.totalPages,
                totalSoldItems: action.payload.totalSold,
            };
        case LOAD_SEARCH_RESULTS_FAIL:
        case LOAD_CATEGORY_RESULTS_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        case SET_USE_GEO_LOCATION:
            return {
                ...state,
                useGeoLocation: action.payload,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.search;
const globalStateSelector = (state: GlobalState) => state;

const getUseGeoLocation = createSelector(stateSelector, state => state.useGeoLocation);

export const getSearchedItemIds = createSelector(stateSelector, state => state.itemIds || []);

export const getSearchedItems = createSelector([getSearchedItemIds, globalStateSelector], (itemIds, state) =>
    getItemCardDatas(state, itemIds)
);

export const getSearchUiState = createSelector(stateSelector, state => {
    return {
        error: state.error,
        isLoading: state.isLoading,
        soldItemIds: state.soldItemIds || [],
        sort: state.sort,
        submitted: state.submitted,
        totalBuyNowItems: state.totalBuyNowItems,
        totalFound: state.totalFound,
        totalLiveItems: state.totalLiveItems,
        totalPages: state.totalPages,
        totalSoldItems: state.totalSoldItems,
    };
});

// For each key in the exclusions object, find the raw facet with that key, and add
// it to the search facets array
export const getSearchFacets = (state: GlobalState, archivedSearch: any, recommend: any) => {
    const { search } = state;
    const facetExclusions = getUserExcludedFacets(state) || [];
    const rawFacets = getRawSearchFacets(state, archivedSearch) || [];
    // $FlowFixMe
    const facets = (!recommend ? cloneDeep(search.facets) : cloneDeep(search.soldItemFacets)) || [];

    // for each exclusion, find the corresponding raw facet option, and add it
    Object.keys(facetExclusions).forEach(complexFacetId => {
        const index = facets.findIndex(f => f.id === complexFacetId);
        const complexFacet = rawFacets.find(f => f.id === complexFacetId);
        if (index === -1 || !complexFacet || !complexFacet.options) return;

        // add excluded options to the options present in the search results
        facets[index].options = facetExclusions[complexFacetId].reduce((options, exclusion) => {
            const includedOptionsIndex = options.findIndex(o => o.id === exclusion);
            const rawFacetOption = complexFacet.options.find(o => o.id === exclusion);
            if (!rawFacetOption) return options;
            if (includedOptionsIndex === -1) return [rawFacetOption, ...options];
            options.splice(includedOptionsIndex, 1, rawFacetOption);
            return options;
        }, facets[index].options || []);
    });

    return annotateFacetExclusions(facets, facetExclusions);
};

const getSelectedOptionFacetValues = (state, archivedSearch, id) => {
    const facets = getSearchFacets(state, archivedSearch);
    const facetValues = facets.find(f => f.id === id);
    const selectedFacetsValues = facetValues && facetValues.options ? facetValues.options.find(f => f.selected) : {};
    return selectedFacetsValues || {};
};

const getSelectedTreeFacetValues = (state, archivedSearch, id) => {
    const facets = getSearchFacets(state, archivedSearch);
    const categoryFacets = facets.find(f => f.id === id);
    const category =
        categoryFacets && categoryFacets.categories.facets.length > 0
            ? categoryFacets.categories.facets.find(f => f.selected)
            : {};
    return category || {};
};

export const getSoldFacetsSelectedCreator = (state: GlobalState) => {
    // $FlowFixMe
    const facet = getSearchFacets(state, false, true)?.find(x => x.id === 'creator');
    if (!facet) return [];
    const { options: creators } = facet;
    return creators.find(c => c.selected);
};

export const getSoldItemImportantCreators = (state: GlobalState) => {
    const { search } = state;
    // $FlowFixMe
    const facet = getSearchFacets(state, false, true)?.find(x => x.id === 'creator');
    if (!facet) return [];
    const { totalSoldItems = 0 } = search;
    const { options: creators } = facet;
    return cloneDeep(creators.filter(creator => creator.count / totalSoldItems >= 0.1));
};

export const getSoldItemImportantStylePeriod = (state: GlobalState) => {
    const { search } = state;
    // $FlowFixMe
    const facet = getSearchFacets(state, false, true)?.find(x => x.id === 'stylePeriod');
    if (!facet) return [];
    const { totalSoldItems = 0 } = search;
    const { options: creators } = facet;
    return cloneDeep(creators.filter(creator => creator.count / totalSoldItems >= 0.1));
};

// F1 indicates category facet
export const getSoldItemImportantCategories = (state: GlobalState) => {
    const { search } = state;
    // $FlowFixMe
    const categoryFacet = getSearchFacets(state, false, true)?.find(x => x.id === 'F1');
    if (!categoryFacet) return [];
    const { categories: facets } = categoryFacet;
    const { totalSoldItems = 0 } = search;

    let categories = [];
    getAllCategories(categories, facets, 0);
    // $FlowFixMe
    return categories
        .filter(category => category.count / totalSoldItems >= 0.1)
        .sort((a, b) => b.count - a.count)
        .sort((a, b) => b.level - a.level);
};

// F2 indicates origin facet
export const getSoldItemImportantOrigins = (state: GlobalState) => {
    const { search } = state;
    // $FlowFixMe
    const originFacet = getSearchFacets(state, false, true)?.find(x => x.id === 'F2');
    if (!originFacet) return [];
    const { categories: facets } = originFacet;
    const { totalSoldItems = 0 } = search;

    let categories = [];
    getAllCategories(categories, facets, 0);
    // $FlowFixMe
    return categories
        .filter(category => category.count / totalSoldItems >= 0.1)
        .sort((a, b) => b.count - a.count)
        .sort((a, b) => b.level - a.level);
};

const getAllCategories = (returnCategories: any[], facets: any[], level: number) => {
    level++;
    facets.forEach(({ categories, count, id, name }) => {
        returnCategories.push({ count, id, level, name });
        if (categories) {
            getAllCategories(returnCategories, categories, level);
        }
    });
};

export const getSelectedCreator = (state: GlobalState, archivedSearch: any) =>
    getSelectedOptionFacetValues(state, archivedSearch, facetTypes.CREATOR_ID);

export const getSelectedMaterials = (state: GlobalState, archivedSearch: any) =>
    getSelectedOptionFacetValues(state, archivedSearch, facetTypes.MATERIALS_AND_TECHNIQUES_ID);

export const getSelectedStyle = (state: GlobalState, archivedSearch: any) =>
    getSelectedOptionFacetValues(state, archivedSearch, facetTypes.STYLE_PERIOD_ID);

export const getSelectedOrigin = (state: GlobalState, archivedSearch: any) =>
    getSelectedTreeFacetValues(state, archivedSearch, facetTypes.ORIGIN_ID);

export const getSelectedCategory = (state: GlobalState, archivedSearch: any) =>
    getSelectedTreeFacetValues(state, archivedSearch, facetTypes.CATEGORY_ID);

export const getUpcomingAuctions = (state: GlobalState, archivedSearch: any) => {
    const facets = getSearchFacets(state, archivedSearch);
    const facetValues = facets.find(f => f.id === facetTypes.AUCTION_HOUSE_ID);
    return facetValues ? facetValues.options.length : 0;
};

export const getCategoryWithGreatestResults = (state: GlobalState, archivedSearch: any) => {
    const facets = getSearchFacets(state, archivedSearch);
    const categoryFacets = facets.find(f => f.id === facetTypes.CATEGORY_ID);
    const category =
        categoryFacets && categoryFacets.categories && categoryFacets.categories.facets.length > 0
            ? categoryFacets.categories.facets[0]
            : {};
    return category;
};

/* ACTION CREATORS */
export const submitSearch = (searchQuery: any, pageName: string) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        dispatch({
            payload: searchQuery,
            type: LOAD_SEARCH_RESULTS_REQUEST,
        });

        const authToken = getAuthToken(state);

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const augmentedOptions = !searchExclusions
            ? searchQuery.options
            : Object.keys(searchExclusions).reduce(
                  (options, facetWithExclusions) => ({
                      ...options,
                      [facetWithExclusions]: [
                          {
                              exclude: searchExclusions[facetWithExclusions] || [],
                              include: searchQuery.options[facetWithExclusions] || [],
                          },
                      ],
                  }),
                  { ...searchQuery.options }
              );
        const augmentedSearchQuery = {
            ...searchQuery,
            options: augmentedOptions,
        };

        if (augmentedSearchQuery?.options?.auctionHouse) {
            augmentedSearchQuery?.options?.auctionHouse[0]?.exclude?.slice(0, 200);
        }

        if (augmentedSearchQuery?.options?.topRated) {
            augmentedSearchQuery.topRated = true;
        }

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);

        const results = await api.fetchSearchResults({
            authToken,
            deployment,
            geoCoordinates,
            searchQuery: augmentedSearchQuery,
            useGeoLocation,
        });
        if (results.error) {
            dispatch({
                error: true,
                meta: { searchQuery },
                payload: results,
                type: LOAD_SEARCH_RESULTS_FAIL,
            });
        } else {
            dispatch({
                payload: results.payload,
                type: LOAD_SEARCH_RESULTS_SUCCESS,
            });
            if (pageName !== 'catalog') {
                dispatch(searchPromoted({ pageName, searchQuery }));
            }
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { searchQuery },
            payload: error,
            type: LOAD_SEARCH_RESULTS_FAIL,
        });
    }
};

export const setUseGeoLocation = (useGeoLocation: Boolean) => ({
    payload: useGeoLocation,
    type: SET_USE_GEO_LOCATION,
});
